import React, { useState } from 'react';
import {
  Play,
  Power,
  PowerOff,
  Sparkles,
  Wand2,
  Search,
  Settings,
  Box,
  Brain,
  Lightbulb,
  Layers,
  Archive,
  Bookmark,
  Share2,
  Users,
  Network,
  Smartphone,
  GitCompareArrows,
  GanttChart,
  BookOpen,
  FileSearch,
  Combine,
  Copy,
  Folder,
  Pause,
  Trash2,
  Skull,
  GitBranch,
  type LucideIcon,
} from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { skillService, type Skill } from '@/services/skills';
import { notifyExtensionTabAction, requestExtensionCommandPoll } from '@/services/extensionBridge';

const ICON_MAP: Record<string, LucideIcon> = {
  Wand2,
  GitCompareArrows,
  GanttChart,
  BookOpen,
  Search,
  FileSearch,
  Network,
  Lightbulb,
  Settings,
  Pause,
  Copy,
  Folder,
  Combine,
  Sparkles,
  Box,
  Smartphone,
  Zap: Sparkles,
  Skull,
  Layers,
  Archive,
  Bookmark,
  GitBranch,
  Share2,
  Users,
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string; label: string }> = {
  generate: { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-500/30', label: 'Generate' },
  analyze:  { bg: 'bg-blue-500/10',    text: 'text-blue-600 dark:text-blue-400',       border: 'border-blue-500/30',    label: 'Analyze' },
  organize: { bg: 'bg-violet-500/10',  text: 'text-violet-600 dark:text-violet-400',   border: 'border-violet-500/30',  label: 'Organize' },
  predict:  { bg: 'bg-amber-500/10',   text: 'text-amber-600 dark:text-amber-400',     border: 'border-amber-500/30',   label: 'Predict' },
  optimize: { bg: 'bg-indigo-500/10',  text: 'text-indigo-600 dark:text-indigo-400',   border: 'border-indigo-500/30',  label: 'Optimize' },
  collaborate: { bg: 'bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400',       border: 'border-rose-500/30',    label: 'Collaborate' },
};

interface SkillCardProps {
  skill: Skill;
  onChange?: (skill: Skill) => void;
  onRun?: (skill: Skill) => Promise<void> | void;
  /** When true, Run executes the skill even if it has a frontend_route (e.g. on that page already). */
  preferExecute?: boolean;
}

export const SkillCard: React.FC<SkillCardProps> = ({ skill, onChange, onRun, preferExecute }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(false);

  const Icon = (skill.icon && ICON_MAP[skill.icon]) || Sparkles;
  const cat = CATEGORY_COLORS[skill.category] || CATEGORY_COLORS.analyze;

  const handleRun = async () => {
    if (onRun) {
      setBusy(true);
      try {
        await onRun(skill);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (skill.frontend_route && !preferExecute) {
      navigate(skill.frontend_route);
      return;
    }
    setBusy(true);
    try {
      const params = { ...(skill.params ?? {}) };
      notifyExtensionTabAction(skill.id, params);
      const res = await skillService.executeSkill(skill.id, params);
      requestExtensionCommandPoll();
    if (res.status === 'success') {
      const data = res.data ?? {};
      if (skill.id === 'agent_list' && typeof data.count === 'number') {
        toast.success(`Listed ${data.count} agent(s) in the orchestration pool`);
      } else if (skill.id === 'agent_details' && data.agent && typeof data.agent === 'object') {
        const agent = data.agent as { name?: string };
        toast.success(`Loaded details for ${agent.name ?? 'agent'}`);
      } else if (skill.id === 'agent_skill_runs' && typeof data.count === 'number') {
        toast.success(`Loaded ${data.count} recent run(s)`);
      } else if (skill.id === 'analyze_tab' && typeof data.summary === 'string' && data.summary) {
        toast.success(data.summary.length > 120 ? `${data.summary.slice(0, 117)}…` : data.summary);
      } else {
        toast.success(res.message || `Skill "${skill.name}" executed`);
      }
      } else if (res.status === 'skipped') {
        toast.message(res.message || 'Skill has no executor; deep-link only.');
      } else {
        toast.error(`Skill "${skill.name}" failed`);
      }
    } catch (e) {
      toast.error(`Failed to run ${skill.name}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      const updated = skill.is_enabled
        ? await skillService.disableSkill(skill.id)
        : await skillService.enableSkill(skill.id);
      onChange?.(updated);
      toast.success(`${skill.name} ${updated.is_enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(`Toggle failed: ${(e as Error).message}`);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div
      className={`p-4 bg-khiimori-bg-elevated border rounded-xl transition-shadow hover:shadow-md ${cat.border} ${!skill.is_enabled ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start gap-3 mb-2">
        <div className={`p-2 rounded-lg ${cat.bg} ${cat.text}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-khiimori-text-primary truncate">{skill.name}</h4>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cat.bg} ${cat.text}`}>
              {cat.label}
            </span>
          </div>
          <p className="text-[11px] font-mono text-khiimori-text-muted mt-0.5">{skill.id}</p>
        </div>
        <Tooltip content={skill.is_enabled ? 'Disable skill' : 'Enable skill'}>
          <button
            onClick={handleToggle}
            disabled={toggling}
            className={`p-1.5 rounded-md transition-colors ${skill.is_enabled ? 'text-green-500 hover:bg-green-500/10' : 'text-khiimori-text-muted hover:bg-khiimori-bg-elevated/30'} ${toggling ? 'opacity-50' : ''}`}
          >
            {skill.is_enabled ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
          </button>
        </Tooltip>
      </div>

      <p className="text-xs text-khiimori-text-secondary line-clamp-3 mb-3 min-h-[3em]">
        {skill.description}
      </p>

      <div className="flex items-center gap-2 text-[10px] text-khiimori-text-muted mb-3">
        <span className="px-1.5 py-0.5 rounded bg-khiimori-bg-elevated/40">{skill.agent}</span>
        <span className="px-1.5 py-0.5 rounded bg-khiimori-bg-elevated/40">{skill.trigger}</span>
        {skill.confidence > 0 && (
          <span className="px-1.5 py-0.5 rounded bg-khiimori-bg-elevated/40">
            conf {Math.round(skill.confidence * 100)}%
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleRun}
          disabled={busy || !skill.is_enabled}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${skill.is_enabled ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-khiimori-bg-elevated/30 text-khiimori-text-muted cursor-not-allowed'} ${busy ? 'opacity-50' : ''}`}
        >
          <Play className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
          {onRun || preferExecute || !skill.frontend_route ? 'Run' : 'Open'}
        </button>
        {skill.frontend_route && (
          <Tooltip content={`Open in app: ${skill.frontend_route}`}>
            <button
              onClick={() => navigate(skill.frontend_route!)}
              className="p-1.5 rounded-md text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/30"
            >
              <Brain className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default SkillCard;
